import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import { Course } from '../../types/course';
import SuggestedCoursesCarousel from './SuggestedCoursesCarousel';
import { cardTheme } from '../../styles/cardTheme';

interface SuggestedCoursesSectionProps {
  id?: string;
  className?: string;
}

const SuggestedCoursesSection: React.FC<SuggestedCoursesSectionProps> = ({
  id = 'suggested-courses-section',
  className = 'relative py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-green-50/40 via-white to-gold-50/30',
}) => {
  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  useEffect(() => {
    const fetchRecommendedCourses = async () => {
      try {
        setLoadingCourses(true);
        const response = await axios.get<{
          success: boolean;
          message?: string;
          data?: {
            data: Course[];
          };
        }>(`${BASE_URL}api/Course/suggested`, {
          params: { pageNumber: 1, pageSize: 6 },
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data?.data?.data) {
          setRecommendedCourses(response.data.data.data);
        }
      } catch (error) {
        console.error('Error fetching recommended courses:', error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchRecommendedCourses();
  }, []);

  return (
    <section id={id} className={className}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
          {/* Title — right side in RTL */}
          <div className="w-full shrink-0 text-center lg:w-[20%] xl:w-[18%] lg:text-right lg:pe-3">
            <span className={`inline-flex items-center gap-2 ${cardTheme.badgeGold} mb-4`}>
              مختاراتنا ليك
            </span>
            <h2
              className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 leading-tight ${cardTheme.title}`}
            >
              كورسات هتفرق معاك
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-md lg:max-w-none lg:ms-auto leading-relaxed">
              المنصة مجمعة لك أهم الكورسات والحصص هنا
            </p>
          </div>

          {/* Carousel — left in RTL, fits 3 cards */}
          <div className="w-full min-w-0 overflow-x-hidden lg:flex-1 lg:max-w-[85%] xl:max-w-[88%]">
            {!loadingCourses && recommendedCourses.length === 0 ? (
              <div className="text-center py-12 lg:text-right">
                <p className="text-gray-500">لا توجد كورسات مقترحة حالياً</p>
              </div>
            ) : (
              <SuggestedCoursesCarousel
                courses={recommendedCourses}
                loading={loadingCourses}
                compact
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuggestedCoursesSection;
